import axios, { AxiosResponse, AxiosError } from 'axios';
import { getBffBaseUrl } from '../utils/bffBaseUrl';
import { getCsrfToken, getCsrfTokenFromCookie } from '../utils/csrfToken';
declare const window: any;

const BASE_URL = getBffBaseUrl();

// Custom event for session expiration
export const SESSION_EXPIRED_EVENT = 'sessionExpired';

const dispatchSessionExpired = () => {
  const event = new CustomEvent(SESSION_EXPIRED_EVENT);
  window.dispatchEvent(event);
};

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const shouldFetchCsrfForRequest = (config: any) => {
  const method = String(config.method || 'get').toLowerCase();
  return (
    ['post', 'put', 'patch', 'delete'].includes(method) ||
    String(config.url || '').includes('/api/v1/auth/refresh') ||
    String(config.url || '').includes('/auth/refresh')
  );
};

api.interceptors.request.use(async (config) => {
  window.dispatchEvent(new Event('api-activity'));
  let csrfToken = getCsrfTokenFromCookie();
  if (!csrfToken && shouldFetchCsrfForRequest(config)) {
    csrfToken = await getCsrfToken(BASE_URL);
  }
  if (csrfToken) {
    config.headers['x-csrf-token'] = csrfToken;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  refreshQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Skip handling for logout endpoint to avoid loops
    if (originalRequest?.url?.includes('auth/logout')) {
      return Promise.reject(error);
    }

    // Handle 403 cross-tenant isolation rejections. The BFF returns these when
    // the session's tenant != the vanity this request was issued from (or the
    // vanity can't be confirmed) — i.e. you're signed into one tenant but
    // viewing another tenant's site. Force a fresh sign-in on the CURRENT vanity
    // (dispatchSessionExpired restarts SSO on this host) rather than surfacing
    // wrong-tenant data or a raw error.
    if (error.response?.status === 403) {
      const code = (error.response?.data as any)?.detail?.error_code;
      if (code === 'VANITY_TENANT_MISMATCH' || code === 'VANITY_TENANT_UNRESOLVED') {
        dispatchSessionExpired();
        return Promise.reject(error);
      }
    }

    // Handle 401 errors (Unauthorized)
    if (error.response?.status === 401) {
      const responseData = error.response?.data as any;
      const isTokenExpired = responseData?.detail?.error_code === 'TOKEN_EXPIRED';
      const isRefreshEndpoint = originalRequest?.url?.includes('auth/refresh');

      if (!isRefreshEndpoint && isTokenExpired) {
        // If a refresh is already in flight, queue this request to retry after it resolves
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          }).then(() => api(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await api.get(`${BASE_URL}/api/v1/auth/refresh`);
          processQueue(null);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          dispatchSessionExpired();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else if (!isRefreshing) {
        // Only dispatch session expired if we're not mid-refresh
        dispatchSessionExpired();
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

async function handleRequest(promise: Promise<AxiosResponse<any>>) {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    throw error;
  }
}


export interface TenantInfo {
  tenantId: string;
  vanity: string | null;
  companyName: string;
  displayName: string;
  subscriptionTier: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  themeColor: string | null;
  loginCopy: string | null;
  postLoginUrl: string | null;
  showChatButton: boolean;
  showAppsButton: boolean;
}

export const getTenantInfo = (): Promise<TenantInfo> => handleRequest(api.get(`${BASE_URL}/api/v1/tenant/me`));

export const getUser = () => handleRequest(api.get(`${BASE_URL}/api/v1/user`));

export const getAllUsers = () => handleRequest(api.get(`${BASE_URL}/api/v1/chat/users`));

export const getAllAgents = () => handleRequest(api.get(`${BASE_URL}/api/v1/chat/agents`));

export const getInformation = () => handleRequest(api.get(`${BASE_URL}/api/v1/chat/me`));

export const getNegotiate = () => handleRequest(api.get(`${BASE_URL}/api/v1/chat/negotiate`));

export const getSidePanel = () => handleRequest(api.get(`${BASE_URL}/api/v1/collaborations/sidebar`));

export const getMessages = async (collaborationId: string, continuationToken?: string) => {
  const headers: Record<string, string> = {};
  if (continuationToken) {
    headers['x-continuation-token'] = continuationToken;
  }
  const response = await api.get(
    `${BASE_URL}/api/v1/chat/history?collaborationId=${collaborationId}`,
    { headers }
  );
  const responseToken = (response.headers['x-continuation-token'] as string) || null;
  return {
    messages: (response.data.messages || []).filter((msg: any) => msg.sender),
    hasMore: response.data.hasMore ?? false,
    continuationToken: responseToken,
  };
};

export const getAttachments = (collaborationId: string) =>
  handleRequest(api.get(`${BASE_URL}/api/v1/chat/attachments?collaborationId=${collaborationId}`));

export const listMembers = (groupId: string) =>
  handleRequest(api.get(`${BASE_URL}/api/v1/groups/${groupId}/members`));

export const sendMessage = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/send`, data));

export const toggleReaction = (data: {
  collaboration_id: string;
  message_id: string;
  emoji: string;
  action: string;
  feedback: string;
  rating: any;
}) => handleRequest(api.post(`${BASE_URL}/api/v1/chat/react`, data));

export const readChat = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/read`, data));

export const leaveChat = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/leave`, data));

export const addMember = (groupId: string, data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/groups/${groupId}/members`, data));

export const removeMember = (groupId: string, userId: string) =>
  handleRequest(api.delete(`${BASE_URL}/api/v1/groups/${groupId}/members/${userId}`));

export const renameChat = (data: any) =>
  handleRequest(api.put(`${BASE_URL}/api/v1/chat/rename`, data));

export const removeUser = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/remove`, data));

export const addUser = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/invite`, data));

export const addAgent = (collaborationId: string, agentUid: string) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/collaborations/${collaborationId}/invite-agent`, { agentUid }));

export const removeAgent = (collaborationId: string, agentUid: string) =>
  handleRequest(api.delete(`${BASE_URL}/api/v1/chat/collaborations/${collaborationId}/remove-agent`, { data: { agentUid } }));

export const createChat = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/create`, data));

export const createInstance = (data: { collaborationId: string; topic: string }) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/instance`, data));

export const renameInstance = (data: { collaborationId: string; collabInstanceId: string; topic: string }) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/instance/topic`, data));

export const deleteInstance = (collaborationId: string, collabInstanceId: string) =>
  handleRequest(api.delete(`${BASE_URL}/api/v1/chat/instance?collaborationId=${collaborationId}&collabInstanceId=${collabInstanceId}`));

export const getFileURL = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/file-download`, data));

export const getRagSourceURL = (data: any) =>
  handleRequest(api.post(`${BASE_URL}/api/v1/chat/rag-source-download`, data));

export const getRagSourceBlob = async (data: any): Promise<Blob> => {
  const response = await api.post(`${BASE_URL}/api/v1/chat/rag-source-content`, data, {
    responseType: 'blob',
  });
  return response.data;
};

// globalLogout=true routes through Microsoft/Entra's logout endpoint so the IdP
// session is terminated and the next sign-in re-prompts. Local-only logout
// (the default) just clears the BFF cookies, which leaves the Entra SSO session
// alive and lets the next sign-in complete silently.
export const logout = (globalLogout = false) =>
  handleRequest(
    api.post(`${BASE_URL}/api/v1/auth/logout${globalLogout ? '?global=true' : ''}`),
  );

export const uploadFile = (file: File, groupId: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('collaboration_id', groupId);
  return handleRequest(
    api.post(`${BASE_URL}/api/v1/chat/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  );
};
